/**
 * Worker-level employment classification helpers (frontend mirror of
 * backend/shared/utils/employment.py — keep the two in sync).
 *
 * ICA (independent contractor), Casual and Learnership workers do NOT complete
 * performance reviews, so they are excluded from objective-setting, scheduled
 * check-ins, and calibration. They are still shown (separately) in the People
 * Leader's team-at-a-glance for context. Interns and FTC employees STAY in.
 *
 * Authoritative signal is the worker-level `employment_category` (synced from
 * F&O). Until that's populated we fall back to the position-level `position_type`
 * (`PT Temporary (IC)` → ICA, `PT Temporary (Cas)` → Casual). NOTE: Learnership
 * has no position_type fallback — it shares `FT Temporary` with Intern/FTC, so
 * it's only excluded once `employment_category` is synced.
 */

export const REVIEW_EXCLUDED_CATEGORIES = ['ICA', 'Casual', 'Learnership'] as const
export const REVIEW_EXCLUDED_POSITION_TYPES = ['PT Temporary (IC)', 'PT Temporary (Cas)'] as const

/** A team member as far as employment classification is concerned. */
export interface EmploymentClassifiable {
  employment_category?: string | null
  position_type?: string | null
  /** Pre-computed by the backend (manager team-summary). Authoritative when present. */
  review_eligible?: boolean
}

/** True if the worker is an ICA/Casual who does not take part in reviews. */
export function isReviewExcluded(member: EmploymentClassifiable): boolean {
  // Trust the backend's pre-computed flag when present.
  if (typeof member.review_eligible === 'boolean') return !member.review_eligible
  const category = (member.employment_category || '').trim()
  if (category) return (REVIEW_EXCLUDED_CATEGORIES as readonly string[]).includes(category)
  const positionType = (member.position_type || '').trim()
  return (REVIEW_EXCLUDED_POSITION_TYPES as readonly string[]).includes(positionType)
}

/** Inverse of {@link isReviewExcluded} — included in the performance/calibration cycle. */
export function isReviewEligible(member: EmploymentClassifiable): boolean {
  return !isReviewExcluded(member)
}

/** Human label for the excluded category (for the "Contractors" group heading / badge). */
export function employmentCategoryLabel(member: EmploymentClassifiable): string {
  const category = (member.employment_category || '').trim()
  if (category) return category
  if ((member.position_type || '').trim() === 'PT Temporary (IC)') return 'ICA'
  if ((member.position_type || '').trim() === 'PT Temporary (Cas)') return 'Casual'
  return 'Contractor'
}
